/**
 * Booking Validation Service
 *
 * Consolidates validation logic from:
 * - ServiceItem.vue (onGetScheduleAvailability) - lines 524-556
 * - ServiceItem.vue (onValidatePaxAmount) - lines 757-769
 *
 * DRY: Single source of truth for booking date and participant validation.
 * This is a pure domain service with no Vue dependencies.
 */
// =============================================================================
// SERVICE
// =============================================================================
export const bookingValidationService = {
    /**
     * Validate booking date against service constraints.
     *
     * Checks:
     * - Date is not in the past (unless allowPast is true)
     * - Date respects min_time_booking (earliest allowed date)
     * - Date respects max_time_booking (latest allowed date)
     *
     * @param config - Validation configuration
     * @returns Validation result with error message if invalid
     */
    validateBookingDate(config) {
        const { selectedDate, minDays = 0, maxDays = 0, allowPast = false } = config;
        // No date selected
        if (!selectedDate) {
            return {
                valid: false,
                error: 'Debe seleccionar una fecha',
            };
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const bookingDate = parseBookingDate(selectedDate);
        bookingDate.setHours(0, 0, 0, 0);
        // Check past date (unless loading existing itinerary)
        if (!allowPast && bookingDate < today) {
            return {
                valid: false,
                error: 'No se puede reservar en fechas pasadas',
                isPastDate: true,
            };
        }
        // Check minimum booking window
        if (minDays > 0) {
            const earliestAllowed = new Date(today);
            earliestAllowed.setDate(today.getDate() + minDays);
            if (bookingDate < earliestAllowed) {
                return {
                    valid: false,
                    error: `La reserva debe hacerse con al menos ${minDays} día(s) de anticipación`,
                    isTooEarly: true,
                };
            }
        }
        // Check maximum booking window
        if (maxDays > 0) {
            const latestAllowed = new Date(today);
            latestAllowed.setDate(today.getDate() + maxDays);
            if (bookingDate > latestAllowed) {
                return {
                    valid: false,
                    error: `La reserva no puede exceder los ${maxDays} días en el futuro`,
                    isTooLate: true,
                };
            }
        }
        return { valid: true };
    },
    /**
     * Validate number of participants against capacity constraints.
     *
     * Checks:
     * - Quantity is greater than 0
     * - Quantity does not exceed total capacity (standard + overcapacity)
     *
     * @param config - Validation configuration
     * @returns Validation result with overbooking flag
     */
    validateParticipants(config) {
        const { quantity, standardCapacity, overcapacity = null } = config;
        // Calculate total capacity
        const totalCapacity = standardCapacity + (overcapacity || 0);
        // Check minimum
        if (quantity <= 0) {
            return {
                valid: false,
                error: 'La cantidad de participantes debe ser mayor a 0',
                isOverbooking: false,
            };
        }
        // Check maximum capacity
        if (quantity > totalCapacity) {
            return {
                valid: false,
                error: `La cantidad de participantes no puede exceder ${totalCapacity}`,
                isOverbooking: false,
                exceedsCapacity: true,
            };
        }
        // Determine if using overbooking
        const isOverbooking = overcapacity != null && quantity > standardCapacity;
        return {
            valid: true,
            isOverbooking,
        };
    },
    /**
     * Check if the selected date is within the valid booking window.
     * Utility method for quick boolean check.
     *
     * @param selectedDate - Date string in YYYY-MM-DD format
     * @param minDays - Minimum days before booking
     * @param maxDays - Maximum days in advance
     * @returns True if date is within valid range
     */
    isDateInBookingWindow(selectedDate, minDays, maxDays) {
        const result = this.validateBookingDate({
            selectedDate,
            minDays,
            maxDays,
            allowPast: false,
        });
        return result.valid;
    },
    /**
     * Get human-readable booking window message.
     *
     * @param minDays - Minimum days before booking
     * @param maxDays - Maximum days in advance
     * @returns Descriptive message
     */
    getBookingWindowMessage(minDays, maxDays) {
        const parts = [];
        if (minDays > 0) {
            parts.push(`mínimo ${minDays} día(s) de anticipación`);
        }
        if (maxDays > 0) {
            parts.push(`máximo ${maxDays} días en el futuro`);
        }
        if (parts.length === 0) {
            return 'Sin restricciones de fecha';
        }
        return `Reserva: ${parts.join(', ')}`;
    },
    /**
     * Get capacity message for display.
     *
     * @param standardCapacity - Standard capacity
     * @param overcapacity - Additional overcapacity (null if not enabled)
     * @returns User-friendly capacity message
     */
    getCapacityMessage(standardCapacity, overcapacity) {
        if (overcapacity && overcapacity > 0) {
            const total = standardCapacity + overcapacity;
            return `Capacidad: ${standardCapacity} estándar + ${overcapacity} extra (máx. ${total})`;
        }
        return `Capacidad máxima: ${standardCapacity} participantes`;
    },
    /**
     * Calculate the earliest allowed booking date.
     *
     * @param minDays - Minimum days before booking
     * @returns Date object for earliest allowed date
     */
    getEarliestBookingDate(minDays) {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() + minDays);
        return date;
    },
    /**
     * Calculate the latest allowed booking date.
     *
     * @param maxDays - Maximum days in advance
     * @returns Date object for latest allowed date
     */
    getLatestBookingDate(maxDays) {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() + maxDays);
        return date;
    },
};
function parseBookingDate(value) {
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnlyMatch) {
        const [, year, month, day] = dateOnlyMatch;
        return new Date(Number(year), Number(month) - 1, Number(day));
    }
    return new Date(value);
}
// =============================================================================
// DEFAULT EXPORT
// =============================================================================
export default bookingValidationService;
